import { LocalizationService } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import { EmailDto } from 'src/core/models/email/email.dto';
import { EmailDirection } from 'src/core/models/generic-lookup-type/email/email-direction.glt';
import { EmailReplyStatus } from 'src/core/models/enum/email-reply-status.enum';

type DurationUnit = 'd' | 'h' | 'm' | 's';

interface DurationPart {
  unit: DurationUnit;
  value: number;
}

const SECONDS_PER_DAY = 86400;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

/**
 * Formats a duration expressed in seconds into a compact, human readable string
 * (e.g. "14m 32s", "2d 4h 15m"). Reused across the Reply Time column, the
 * conversations-list Reply Time column and the conversation-detail Thread tab label.
 */
@Injectable({
  providedIn: 'root',
})
export class DurationFormatHelper {
  constructor(private localizationService: LocalizationService) {}

  /**
   * Converts a total number of seconds into a compact duration string using the
   * largest applicable tier (day / hour / minute / second).
   * @param totalSeconds total duration in seconds
   * @returns compact duration string, e.g. '1h 02m 10s'
   */
  formatCompactDuration(totalSeconds: number): string {
    const parts = this.getParts(totalSeconds);
    const visibleParts = this.filterZeroParts(parts);

    return visibleParts.map((part, index) => this.formatPart(part, index === 0)).join(' ');
  }

  /**
   * Computes the Reply Time display value for a received email, following the same
   * 4-cell rule used by the conversations-list Reply Time column and the
   * conversation-detail Thread tab.
   * @param email the email to compute the reply time display for
   * @returns compact duration string, '—' for not-applicable/resolved-without-time cases,
   * or an empty string while a reply is still pending
   */
  getEmailReplyTimeDisplay(email: EmailDto): string {
    if (email == null || email.directionId !== EmailDirection.received) {
      return '—';
    }

    if (email.replyStatus === EmailReplyStatus.ReplyNeeded && email.replyTimeSeconds == null) {
      return '';
    }

    if (
      email.replyStatus === EmailReplyStatus.ReplyNotNeeded ||
      email.replyStatus === EmailReplyStatus.ReplyTimeout
    ) {
      return '—';
    }

    if (email.replyTimeSeconds != null) {
      return this.formatCompactDuration(email.replyTimeSeconds);
    }

    return '—';
  }

  private getParts(totalSeconds: number): DurationPart[] {
    if (totalSeconds >= SECONDS_PER_DAY) {
      const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
      const remainderAfterDays = totalSeconds % SECONDS_PER_DAY;
      const hours = Math.floor(remainderAfterDays / SECONDS_PER_HOUR);
      const minutes = Math.floor((remainderAfterDays % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);

      return [
        { unit: 'd', value: days },
        { unit: 'h', value: hours },
        { unit: 'm', value: minutes },
      ];
    }

    if (totalSeconds >= SECONDS_PER_HOUR) {
      const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
      const remainderAfterHours = totalSeconds % SECONDS_PER_HOUR;
      const minutes = Math.floor(remainderAfterHours / SECONDS_PER_MINUTE);
      const seconds = Math.floor(remainderAfterHours % SECONDS_PER_MINUTE);

      return [
        { unit: 'h', value: hours },
        { unit: 'm', value: minutes },
        { unit: 's', value: seconds },
      ];
    }

    if (totalSeconds >= SECONDS_PER_MINUTE) {
      const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
      const seconds = Math.floor(totalSeconds % SECONDS_PER_MINUTE);

      return [
        { unit: 'm', value: minutes },
        { unit: 's', value: seconds },
      ];
    }

    return [{ unit: 's', value: Math.floor(totalSeconds) }];
  }

  private filterZeroParts(parts: DurationPart[]): DurationPart[] {
    const nonZeroParts = parts.filter(part => part.value !== 0);
    return nonZeroParts.length > 0 ? nonZeroParts : [parts[parts.length - 1]];
  }

  private formatPart(part: DurationPart, isLeading: boolean): string {
    const isPaddable = part.unit !== 'd' && part.unit !== 'h';
    const value =
      !isLeading && isPaddable ? part.value.toString().padStart(2, '0') : part.value.toString();

    return value + this.getUnitSuffix(part.unit);
  }

  private getUnitSuffix(unit: DurationUnit): string {
    switch (unit) {
      case 'd':
        return this.localizationService.instant('::DayShort');
      case 'h':
        return this.localizationService.instant('::HourShort');
      case 'm':
        return this.localizationService.instant('::MinuteShort');
      case 's':
        return this.localizationService.instant('::CompactSecondsShort');
    }
  }
}
